import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';

function RaisePOModal({ shortageItems, linkedTo, linkedId, linkedNumber, onClose, onSuccess }) {
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  useEffect(() => {
    fetchWarehouses();
    initializeItems();
  }, []);
  
  const fetchWarehouses = async () => {
    try {
      const response = await apiFetch('/api/warehouses');
      const data = await response.json();
      setWarehouses(data);
      const defaultWh = data.find(w => w.is_default) || data[0];
      setSelectedWarehouse(defaultWh?.id);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  };
  
  const initializeItems = () => {
    const itemsWithQuantity = shortageItems.map(item => ({
      ...item,
      order_quantity: item.shortage,
      unit_price: 0 // TODO: Could fetch from supplier pricing
    }));
    setItems(itemsWithQuantity);
  };
  
  const handleQuantityChange = (productId, newQty) => {
    setItems(items.map(item => 
      item.product_id === productId 
        ? { ...item, order_quantity: parseFloat(newQty) || 0 }
        : item
    ));
  };
  
  const handlePriceChange = (productId, newPrice) => {
    setItems(items.map(item => 
      item.product_id === productId 
        ? { ...item, unit_price: parseFloat(newPrice) || 0 }
        : item
    ));
  };
  
  const removeItem = (productId) => {
    setItems(items.filter(item => item.product_id !== productId));
  };
  
  const groupBySupplier = () => {
    const groups = {};
    items.forEach(item => {
      if (item.order_quantity > 0) {
        const supplierId = item.supplier_id || 'unknown';
        const supplierName = item.supplier_name || 'No Supplier';
        if (!groups[supplierId]) {
          groups[supplierId] = {
            supplier_id: supplierId,
            supplier_name: supplierName,
            items: [],
            total: 0
          };
        }
        groups[supplierId].items.push(item);
        groups[supplierId].total += item.order_quantity * item.unit_price;
      }
    });
    return Object.values(groups);
  };
  
  const handleSubmit = async () => {
    const supplierGroups = groupBySupplier();
    
    if (supplierGroups.length === 0) {
      alert('Please add at least one item with quantity > 0');
      return;
    }
    
    // Check for items without supplier
    const noSupplier = items.filter(item => item.order_quantity > 0 && !item.supplier_id);
    if (noSupplier.length > 0) {
      alert(`${noSupplier.length} item(s) have no supplier assigned. Please assign suppliers in Products.`);
      return;
    }
    
    setSubmitting(true);
    
    try {
      const endpoint = linkedTo === 'job' 
        ? 'raise-po-from-job'
        : 'raise-po-from-sales-order';
      
      const payload = {
        warehouse_id: selectedWarehouse,
        shortage_items: items.filter(item => item.order_quantity > 0).map(item => ({
          product_id: item.product_id,
          quantity: item.order_quantity,
          unit_price: item.unit_price
        })),
        expected_date: expectedDate || null,
        notes
      };
      
      if (linkedTo === 'job') {
        payload.job_id = linkedId;
        payload.job_number = linkedNumber;
      } else {
        payload.sales_order_id = linkedId;
        payload.sales_order_number = linkedNumber;
      }
      
      const response = await apiFetch(
        `/api/${endpoint}`,
        {
          method: 'POST',
          body: JSON.stringify(payload)
        }
      );
      
      const result = await response.json();
      
      if (response.ok) {
        onSuccess(result);
      } else {
        alert(result.error || 'Failed to create purchase orders');
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Error creating POs:', error);
      alert('Failed to create purchase orders');
      setSubmitting(false);
    }
  };
  
  const supplierGroups = groupBySupplier();
  const totalItems = items.filter(item => item.order_quantity > 0).length;
  const grandTotal = supplierGroups.reduce((sum, g) => sum + g.total, 0);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Raise Purchase Order</h2>
            <p className="text-blue-100 text-sm">
              {linkedTo === 'job' ? `Job #${linkedNumber}` : `Sales Order #${linkedNumber}`}
            </p>
          </div>
          <button onClick={onClose} className="text-white hover:text-blue-100">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Settings */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Purchase Order Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deliver To Warehouse
                </label>
                <select
                  value={selectedWarehouse || ''}
                  onChange={(e) => setSelectedWarehouse(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select warehouse</option>
                  {warehouses.map(wh => (
                    <option key={wh.id} value={wh.id}>
                      {wh.name} {wh.is_default ? '(Default)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expected Delivery
                </label>
                <input
                  type="date"
                  value={expectedDate}
                  onChange={(e) => setExpectedDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
          
          {/* Items to Order */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Items to Order</h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Shortage</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Order Qty</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item, index) => (
                    <tr key={item.product_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 text-sm text-gray-900">{item.product_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.supplier_name || 'No supplier'}</td>
                      <td className="px-4 py-3 text-sm text-right text-red-600 font-medium">
                        {parseFloat(item.shortage).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.order_quantity}
                          onChange={(e) => handleQuantityChange(item.product_id, e.target.value)}
                          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded text-right"
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => handlePriceChange(item.product_id, e.target.value)}
                          className="w-24 px-2 py-1 text-sm border border-gray-300 rounded text-right"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium">
                        £{(item.order_quantity * item.unit_price).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => removeItem(item.product_id)}
                          className="text-red-600 hover:text-red-800"
                          title="Remove from order"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Supplier Grouping Preview */}
          {supplierGroups.length > 0 && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
              <h4 className="font-semibold text-gray-900 mb-2">
                This will create {supplierGroups.length} purchase order{supplierGroups.length !== 1 ? 's' : ''}:
              </h4>
              <div className="space-y-2">
                {supplierGroups.map((group, index) => (
                  <div key={group.supplier_id} className="text-sm">
                    <span className="font-medium text-gray-900">PO #{index + 1}</span> to{' '}
                    <span className="font-semibold text-blue-700">{group.supplier_name}</span>
                    {' '}({group.items.length} item{group.items.length !== 1 ? 's' : ''}, £{group.total.toFixed(2)})
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t">
          <div className="text-sm text-gray-600">
            {totalItems} item{totalItems !== 1 ? 's' : ''} • Grand Total: <span className="font-bold text-gray-900">£{grandTotal.toFixed(2)}</span>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || totalItems === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating...' : `Create ${supplierGroups.length} PO${supplierGroups.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RaisePOModal;
